import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db, storage } from "../firbase/firebase";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";

const COLLECTION = "logo";


export const addLogo = createAsyncThunk(
  "logos/addlogos",
  async ({ occassion, imageFile }, { rejectWithValue }) => {
    try {
      //! upload image to storage
      const fileRef = ref(storage, `logos/${Date.now()}-${imageFile.name}`);
      await uploadBytes(fileRef, imageFile);
      const imageUrl = await getDownloadURL(fileRef);

      //! save url in firestore

      const createdAt = serverTimestamp();
      const active = false;

      const docRef = await addDoc(collection(db, COLLECTION), {
        occassion,
        imageUrl,
        active,
        createdAt,
      });

      return {
        id: docRef.id,
        occassion,
        imageUrl,
        active,
        createdAt,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchLogo = createAsyncThunk(
  "logos/fetchlogos",
  async (_, { rejectWithValue }) => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTION));
      const logos = [];

      querySnapshot.forEach((doc) => {
        logos.push({ id: doc.id, ...doc.data() });
      });

      return logos;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const publishLogo = createAsyncThunk(
  "logos/publishlogos",
  async (logoId, { rejectWithValue }) => {
    try {
      const batch = writeBatch(db);
      const snapshot = await getDocs(collection(db, COLLECTION));

      snapshot.docs.forEach((document) => {
        const docRef = doc(db, COLLECTION, document.id);

        batch.update(docRef, {
          active: document.id === logoId,
        });
      });

      await batch.commit();
      return logoId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPublishedLogo = createAsyncThunk(
  "logos/fetchpublishedlogo",
  async (_, { rejectWithValue }) => {
    try {
      const q = query(collection(db, COLLECTION), where("active", "==", true));

      const snapshot = await getDocs(q);

      const logos = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return logos;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteLogo = createAsyncThunk(
  "logos/deletelogo",
  async ({ id, imageUrl }, { rejectWithValue }) => {
    try {
      //! delete image from firebase storage
      if (imageUrl) {
        const imageRef = ref(storage, imageUrl);
        await deleteObject(imageRef);
      }

      //! delete firestore document
      await deleteDoc(doc(db, COLLECTION, id));
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateLogo = createAsyncThunk(
  "logos/updatelogo",
  async (
    { id, occassion, newImageFile, oldImageFile },
    { rejectWithValue }
  ) => {
    try {
      let updatedData = { occassion };

      //! replace image if new image is provided
      if (newImageFile) {
        //!delete old image
        if (oldImageFile) {
          const oldImageRef = ref(storage, oldImageFile);
          await deleteObject(oldImageRef);
        }

        const newImageRef = ref(
          storage,
          `logos/${Date.now()}-${newImageFile.name}`
        );
        await uploadBytes(newImageRef, newImageFile);
        const newImageUrl = await getDownloadURL(newImageRef);

        updatedData.imageUrl = newImageUrl;
      }

        updatedData.updatedAt = serverTimestamp();

      //! update firestore document
      await updateDoc(doc(db, COLLECTION, id), updatedData);

      return { id, ...updatedData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const LogoSlice = createSlice({
  name: "logo",
  initialState: {
    logo: [],
    publishedLogo: [],
    loading: {
      fetch: false,
      add: false,
      publish: false,
      delete: false,
      update: false,
    },
    error: null,
    editlogo: null,
  },
  reducers: {
    setEditlogo: (state, action) => {
      state.editlogo = action.payload;
    },
    clearEditlogo: (state) => {
      state.editlogo = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addLogo.pending, (state) => {
        state.loading.add = true;
      })
      .addCase(addLogo.fulfilled, (state, action) => {
        state.loading.add = false;
        state.logo.push(action.payload);
      })
      .addCase(addLogo.rejected, (state, action) => {
        state.loading.add = false;
        state.error = action.payload;
      })

      .addCase(fetchLogo.pending, (state) => {
        state.loading.fetch = true;
        state.error = null;
      })
      .addCase(fetchLogo.fulfilled, (state, action) => {
        state.loading.fetch = false;
        state.logo = action.payload;
      })
      .addCase(fetchLogo.rejected, (state, action) => {
        state.loading.fetch = false;
        state.error = action.payload;
      })
      .addCase(publishLogo.pending, (state) => {
        state.loading.publish = true;
        state.error = null;
      })
      .addCase(publishLogo.fulfilled, (state, action) => {
        const publishedId = action.payload;
        state.logo = state.logo.map((item) => ({
          ...item,
          active: item.id === publishedId,
        }));
      })
      .addCase(publishLogo.rejected, (state, action) => {
        state.loading.publish = false;
        state.error = action.payload;
      })
      .addCase(fetchPublishedLogo.pending, (state) => {
        state.loading.publish = true;
      })
      .addCase(fetchPublishedLogo.fulfilled, (state, action) => {
        state.loading.publish = false;
        state.publishedLogo = action.payload;
      })
      .addCase(fetchPublishedLogo.rejected, (state, action) => {
        state.loading.publish = false;
        state.error = action.payload;
      })
      .addCase(deleteLogo.pending, (state) => {
        state.loading.delete = true;
      })
      .addCase(deleteLogo.fulfilled, (state, action) => {
        state.loading.delete = true;

        state.logo = state.logo.filter((item) => item.id !== action.payload);

        state.publishedLogo = state.publishedLogo.filter(
          (item) => item.id !== action.payload
        );
      })

      .addCase(deleteLogo.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = action.payload;
      })
      .addCase(updateLogo.pending, (state) => {
        state.loading.update = true;
      })
      .addCase(updateLogo.fulfilled, (state, action) => {
        state.loading.update = false;

        state.logo = state.logo.map((item) =>
          item.id === action.payload.id ? { ...item, ...action.payload } : item
        );

        state.publishedLogo = state.publishedLogo.map((item) =>
          item.id === action.payload.id ? { ...item, ...action.payload } : item
        );
      })
      .addCase(updateLogo.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload;
      });
  },
});

export const { setEditlogo, clearEditlogo } = LogoSlice.actions;

export default LogoSlice.reducer;
